import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { ArrowLeft, Plus, Search, RefreshCw, Download } from 'lucide-react';
import 'react-toastify/dist/ReactToastify.css';
import MD5 from 'crypto-js/md5';
import * as XLSX from 'xlsx';

// 将UUID缩略显示，保留前8位和后4位，中间用...代替
const abbreviateUuid = (uuid) => {
  if (!uuid || uuid.length <= 12) return uuid;
  return `${uuid.slice(0, 8)}...${uuid.slice(-4)}`;
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddUserModalOpen, setAddUserModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: '',
    password: '',
    isManager: false,
    libraryScope: ['集团制度库'] // Initialize with default selection
  });
  const [newPassword, setNewPassword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 20;
  const [formErrors, setFormErrors] = useState({});
  const [userStats, setUserStats] = useState(null);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [userToToggle, setUserToToggle] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleteUserModalOpen, setDeleteUserModalOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    checkAuthorization();
    fetchUsers();
    fetchUserStats();
  }, []);

  const checkAuthorization = () => {
    const userInfo = JSON.parse(localStorage.getItem('DTC_USER_INFO') || '{}');

    // Redirect if not logged in or not a manager
    if (!userInfo.token || !userInfo.manager_code) {
      toast.error('您没有访问此页面的权限');
      setTimeout(() => navigate('/'), 1500);
      return false;
    }

    return true;
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const userInfo = JSON.parse(localStorage.getItem('DTC_USER_INFO') || '{}');
      const authHeader = `${userInfo.code}@${userInfo.token}`;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/user/users`, {
        headers: {
          'Authorization': authHeader
        }
      });

      const data = await response.json();

      if (data.success) {
        setUsers(data.data || []);
      } else {
        toast.error(data.message || '获取用户列表失败');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('DTC_USER_INFO') || '{}');
      const authHeader = `${userInfo.code}@${userInfo.token}`;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/stat/users`, {
        headers: {
          'Authorization': authHeader
        }
      });

      if (response.status === 200) {
        const data = await response.json();
        setUserStats(data.data[0]);
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  // Pagination logic updated to use users directly instead of filteredUsers
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = users.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(users.length / recordsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // 定义默认的新用户状态
  const defaultNewUser = {
    name: '',
    password: '',
    isManager: false,
    libraryScope: ['集团制度库'] // 默认选中集团制度库
  };

  // 修改打开弹出层的处理函数
  const handleOpenAddUserModal = () => {
    setNewUser({ ...defaultNewUser }); // 重置为默认值
    setFormErrors({}); // 清除表单错误
    setAddUserModalOpen(true);
  };

  // Add new user
  const handleAddUser = async () => {
    setFormErrors({});
    let errors = {};

    // 用户名长度检查
    if (newUser.name.length < 2 || newUser.name.length > 15) {
      errors.name = '用户名长度必须在2到15个字符之间';
    }

    // 密码强度检查
    const passwordPattern = /^(?=(?:[^a-zA-Z]*[a-zA-Z]){1})(?=(?:[^0-9]*[0-9]){1})(?=(?:[^!@#$%^&*]*[!@#$%^&*]){1})[A-Za-z\d!@#$%^&*]{8,}|^(?=(?:[^a-zA-Z]*[a-zA-Z]){1})(?=(?:[^0-9]*[0-9]){1})[A-Za-z\d!@#$%^&*]{8,}|^(?=(?:[^a-zA-Z]*[a-zA-Z]){1})(?=(?:[^!@#$%^&*]*[!@#$%^&*]){1})[A-Za-z\d!@#$%^&*]{8,}|^(?=(?:[^0-9]*[0-9]){1})(?=(?:[^!@#$%^&*]*[!@#$%^&*]){1})[A-Za-z\d!@#$%^&*]{8,}$/;
    if (!passwordPattern.test(newUser.password)) {
      errors.password = '密码必须至少8位，并且包含大小写字母、数字和特殊字符中的两种';
    }

    // 知识库选择检查
    if (newUser.libraryScope.length === 0) {
      errors.libraryScope = '必须授权至少一个知识库';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // 计算 type 值
    let type = 0;
    if (newUser.libraryScope.includes('集团制度库')) {
      type += 1; // 选中集团
    }
    if (newUser.libraryScope.includes('区政策库')) {
      type += 2; // 选中区政策库
    }

    try {
      const userInfo = JSON.parse(localStorage.getItem('DTC_USER_INFO') || '{}');
      const authHeader = `${userInfo.code}@${userInfo.token}`;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/user/users`, {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newUser.name,
          password: MD5(newUser.password).toString(),
          type: type
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('用户创建成功');
        setAddUserModalOpen(false);
        setNewUser({ name: '', password: '', isManager: false, libraryScope: ['集团制度库'] });
        fetchUsers();
        fetchUserStats();
      } else {
        toast.error(data.message || '创建用户失败');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('创建用户失败');
    }
  };

  // Toggle user status (enable/disable)
  const toggleUserStatus = async (userCode, currentStatus, userName) => {
    if (currentStatus === 1) {
      setUserToToggle({ code: userCode, status: currentStatus, name: userName });
      setConfirmModalOpen(true);
      return;
    }

    // If enabling user, proceed directly
    await updateUserStatus(userCode, currentStatus);
  };

  // New function to handle the actual status update
  const updateUserStatus = async (userCode, currentStatus) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('DTC_USER_INFO') || '{}');
      const authHeader = `${userInfo.code}@${userInfo.token}`;

      const newStatus = currentStatus === 1 ? 0 : 1;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/user/users/${userCode}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus
        })
      });

      const data = await response.json();

      if (data.success) {
        // Update just the specific user's status in the state
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.code === userCode
              ? { ...user, status: newStatus }
              : user
          )
        );
        toast.success(`用户${newStatus === 1 ? '启用' : '禁用'}成功`);
      } else {
        toast.error(data.message || `${newStatus === 1 ? '启用' : '禁用'}用户失败`);
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('操作失败');
    }
  };

  // Reset user password
  const handleResetPassword = async () => {
    setFormErrors({});
    let errors = {};

    // Password validation
    if (!selectedUser || !newPassword.trim()) {
      errors.password = '密码不能为空';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const userInfo = JSON.parse(localStorage.getItem('DTC_USER_INFO') || '{}');
      const authHeader = `${userInfo.code}@${userInfo.token}`;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/user/users/${selectedUser.code}/password`, {
        method: 'PUT',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          password: MD5(newPassword).toString()
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('密码重置成功');
        setResetPasswordModalOpen(false);
        setNewPassword('');
        setSelectedUser(null);
      } else {
        toast.error(data.message || '密码重置失败');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('密码重置失败');
    }
  };

  const handleLibraryScopeChange = (scope, checked) => {
    setNewUser(prevState => {
      let newScopes = [...prevState.libraryScope];
      if (checked && !newScopes.includes(scope)) {
        newScopes.push(scope);
      } else if (!checked && newScopes.includes(scope)) {
        newScopes = newScopes.filter(item => item !== scope);
      }
      return { ...prevState, libraryScope: newScopes };
    });
  };

  // Add delete user function
  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const userInfo = JSON.parse(localStorage.getItem('DTC_USER_INFO') || '{}');
      const authHeader = `${userInfo.code}@${userInfo.token}`;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/user/users/${userToDelete.code}`, {
        method: 'DELETE',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success('用户删除成功');
        // 从用户列表中移除被删除的用户
        setUsers(prevUsers => prevUsers.filter(user => user.code !== userToDelete.code));
        setDeleteUserModalOpen(false);
        setUserToDelete(null);
        fetchUserStats(); // 更新用户统计信息
      } else {
        toast.error(data.message || '删除用户失败');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('删除用户失败');
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <ToastContainer />

      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="mr-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">人员管理</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User Stats Card */}
        {userStats && (
          <div className="bg-white shadow rounded-lg mb-6 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-8 flex-1">
                <div className="flex-1 max-w-md">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-900">
                      当前授权最多可分配 <span className="text-green-600">{userStats.user_limit}</span> 个账号，已分配 <span className="text-green-600">{userStats.user_count}</span> 个
                    </span>
                  </div>
                  <div className="overflow-hidden h-2 flex rounded bg-blue-100">
                    <div
                      style={{ width: `${(userStats.user_count / userStats.user_limit) * 100}%` }}
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                    ></div>
                  </div>
                </div>
              </div>
              {userStats.user_count < userStats.user_limit && (
                <button
                  onClick={handleOpenAddUserModal}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Plus size={16} className="mr-1" />
                  新增用户
                </button>
              )}
              <button
                onClick={() => {
                  // 导出用户数据为 Excel
                  const exportData = users.map(user => ({
                    '用户名': user.name,
                    '提问次数': user.chat_count || 0
                  }));

                  const worksheet = XLSX.utils.json_to_sheet(exportData);
                  const workbook = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(workbook, worksheet, '提问统计数据');

                  // 设置列宽
                  worksheet['!cols'] = [
                    { wch: 20 }, // 用户名列宽
                    { wch: 12 }  // 提问次数列宽
                  ];

                  // 生成文件名（包含当前日期）
                  const today = new Date().toISOString().split('T')[0];
                  const fileName = `提问统计数据_${today}.xlsx`;

                  XLSX.writeFile(workbook, fileName);
                  toast.success('数据导出成功');
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ml-3"
              >
                <Download size={16} className="mr-1" />
                数据导出
              </button>
            </div>
          </div>
        )}

        {/* User table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  用户ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  用户名
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  角色
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  知识库范围
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  提问次数
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  最近登录时间
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  状态
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                    正在加载用户数据...
                  </td>
                </tr>
              ) : (
                currentRecords.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" title={user.code}>
                      {abbreviateUuid(user.code)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex flex-wrap gap-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.manager === 1 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                          }`}>
                          {user.manager === 1 ? '管理员' : '普通用户'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex flex-wrap gap-1">
                        {user.type === 1 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#4A475C', color: '#FFFFFF' }}>
                            集团制度库
                          </span>
                        )}
                        {user.type === 2 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#B98A82', color: '#FFFFFF' }}>
                            区政策库
                          </span>
                        )}
                        {user.type === 3 && (
                          <>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#4A475C', color: '#FFFFFF' }}>
                              集团制度库
                            </span>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#B98A82', color: '#FFFFFF' }}>
                              区政策库
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.chat_count || 0} 次
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.last_visit_time ? (
                        user.last_visit_time
                      ) : (
                        <span className="text-red-500">从未登录</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.status === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {user.status === 1 ? '正常' : '已禁用'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {user.manager === 1 ? (
                          <span className="text-gray-300"> 不可操作 </span>
                        ) : (
                          <>
                            {user.status === 1 && (
                              <button
                                onClick={() => {
                                  setSelectedUser(user);
                                  setResetPasswordModalOpen(true);
                                }}
                                className="text-blue-600 hover:text-blue-900 focus:outline-none focus:underline"
                              >
                                重置密码
                              </button>
                            )}
                            <button
                              onClick={() => toggleUserStatus(user.code, user.status, user.name)}
                              className={`${user.status === 1 ? 'text-amber-600 hover:text-amber-900' : 'text-green-600 hover:text-green-900'
                                } focus:outline-none focus:underline`}
                            >
                              {user.status === 1 ? '禁用' : '启用'}
                            </button>
                            {user.status === 0 && (
                              <button
                                onClick={() => {
                                  setUserToDelete(user);
                                  setDeleteUserModalOpen(true);
                                }}
                                className="text-red-600 hover:text-red-900 focus:outline-none focus:underline"
                              >
                                删除
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination controls */}
          {!loading && users.length > 0 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    显示 <span className="font-medium">{indexOfFirstRecord + 1}</span> 到 <span className="font-medium">
                      {indexOfLastRecord > users.length ? users.length : indexOfLastRecord}
                    </span> 条，共 <span className="font-medium">{users.length}</span> 条记录
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                        }`}
                    >
                      <span className="sr-only">上一页</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>

                    {[...Array(totalPages).keys()].map(number => (
                      <button
                        key={number + 1}
                        onClick={() => paginate(number + 1)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === number + 1
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                      >
                        {number + 1}
                      </button>
                    ))}

                    <button
                      onClick={() => paginate(currentPage < totalPages ? currentPage + 1 : totalPages)}
                      disabled={currentPage === totalPages || totalPages === 0}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages || totalPages === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                        }`}
                    >
                      <span className="sr-only">下一页</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add User Modal */}
      {isAddUserModalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">新增用户</h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      用户名
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="请输入用户名"
                      autoComplete="off"
                    />
                    {formErrors.name && <p className="text-red-500 text-sm">{formErrors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      初始密码
                    </label>
                    <input
                      type="password"
                      id="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="请输入初始密码"
                      autoComplete="off"
                    />
                    {formErrors.password && <p className="text-red-500 text-sm">{formErrors.password}</p>}
                  </div>
                  <div>
                    <div className="flex items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        知识库范围
                      </label>
                      <div className="ml-2 p-2 bg-yellow-50 rounded text-sm text-yellow-700">
                        注意：知识库之间的内容不会被交叉检索
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newUser.libraryScope.includes('集团制度库')}
                          onChange={(e) => handleLibraryScopeChange('集团制度库', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">集团制度库</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newUser.libraryScope.includes('区政策库')}
                          onChange={(e) => handleLibraryScopeChange('区政策库', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">区政策库</span>
                      </label>
                      {formErrors.libraryScope && (
                        <p className="text-red-500 text-sm mt-1">{formErrors.libraryScope}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleAddUser}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  确认添加
                </button>
                <button
                  type="button"
                  onClick={() => setAddUserModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  取消
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {isResetPasswordModalOpen && selectedUser && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  重置用户密码
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  您正在重置 <span className="font-medium text-gray-700">{selectedUser.name}</span> 的密码
                </p>
                <div className="mb-4 p-3 bg-yellow-50 rounded-md">
                  <p className="text-sm text-yellow-700">
                    <span className="font-medium">注意：</span>
                    密码修改成功后，该用户的所有登录状态都会立即失效，需要重新登录才可以继续使用系统。
                  </p>
                </div>
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                    新密码
                  </label>
                  <input
                    type="password"
                    id="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入新密码"
                  />
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleResetPassword}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  确认重置
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setResetPasswordModalOpen(false);
                    setNewPassword('');
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  取消
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {isConfirmModalOpen && userToToggle && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  禁用用户
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  您正在禁用用户 <span className="font-medium text-gray-700">{userToToggle.name}</span>
                </p>
                <div className="mb-4 p-3 bg-yellow-50 rounded-md">
                  <p className="text-sm text-yellow-700">
                    <span className="font-medium">注意：</span>
                    禁用后该用户将立即失去访问权限，所有登录状态都会失效。该操作可以随时撤销。
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => {
                    updateUserStatus(userToToggle.code, userToToggle.status);
                    setConfirmModalOpen(false);
                    setUserToToggle(null);
                  }}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  确认禁用
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmModalOpen(false);
                    setUserToToggle(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  取消
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {isDeleteUserModalOpen && userToDelete && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  删除用户
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  您确定要删除用户 <span className="font-medium text-gray-700">{userToDelete.name}</span> 吗？
                </p>
                <div className="mb-4 p-3 bg-red-50 rounded-md">
                  <p className="text-sm text-red-700">
                    <span className="font-medium">警告：</span>
                    此操作不可逆，删除后用户数据将无法恢复。
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteUser}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  确认删除
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDeleteUserModalOpen(false);
                    setUserToDelete(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  取消
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 